import * as FirebaseAuth from "firebase/auth";

import { auth } from "@/firebase/web";
import {
  clearCachedVerifier,
  getOrCreateRecaptchaVerifier,
  mapConfirmError,
  mapSendError,
} from "@/utils/phoneVerification.web";

export type PhoneSignInConfirmation = {
  confirm: (code: string) => Promise<FirebaseAuth.UserCredential>;
};

export type PendingPhoneSignIn = {
  confirmation: PhoneSignInConfirmation;
  phonePrefix: string;
  phoneNumber: string;
  // Timestamp of the successful send, so the UI can compute how much of the
  // resend cooldown has already elapsed when it re-mounts from the stash.
  sentAt: number;
};

type StashedPhoneSignIn = PendingPhoneSignIn & { expiresAt: number };

// Firebase verification IDs and OTP codes expire around 5-6 minutes; keep the
// stash TTL just under that so we never hand back a confirmation that will
// blow up with auth/code-expired.
const STASH_TTL_MS = 5 * 60 * 1000;

let pending: StashedPhoneSignIn | null = null;

export const takePendingPhoneSignIn = (): PendingPhoneSignIn | null => {
  if (!pending) return null;
  if (pending.expiresAt <= Date.now()) {
    pending = null;
    return null;
  }
  const { expiresAt: _e, ...entry } = pending;
  return entry;
};

export const clearPendingPhoneSignIn = () => {
  pending = null;
};

const stashPendingPhoneSignIn = (entry: PendingPhoneSignIn) => {
  pending = { ...entry, expiresAt: Date.now() + STASH_TTL_MS };
};

// Unlike startPhoneVerification (which links a phone to an already-signed-in
// user), this creates a brand-new Firebase Auth account whose primary
// credential is the phone number. Shares the reCAPTCHA verifier and error
// mapping with the existing verification flow.
//
// When `metadata` is provided, the resulting confirmation is stashed in a
// module-level cache so that if the UI hosting the OTP step unmounts (Back
// tap, parent re-render, refresh-in-flight), the next mount can pick up where
// the user left off without triggering a fresh SMS.
export const startPhoneSignIn = async (
  phoneE164: string,
  metadata?: { phonePrefix: string; phoneNumber: string },
): Promise<PhoneSignInConfirmation> => {
  // RecaptchaVerifier holds a one-shot reCAPTCHA token. A stale verifier
  // (from a prior profile-page link, a previous signup attempt, or an HMR
  // cycle) causes /recaptcha/api2/pat to return 401 → sendVerificationCode
  // returns 400. Always start fresh.
  clearCachedVerifier();
  const verifier = getOrCreateRecaptchaVerifier();
  let confirmationResult: FirebaseAuth.ConfirmationResult;
  try {
    confirmationResult = await FirebaseAuth.signInWithPhoneNumber(
      auth,
      phoneE164,
      verifier,
    );
  } catch (error) {
    clearCachedVerifier();
    throw mapSendError(error);
  }

  const confirmation: PhoneSignInConfirmation = {
    confirm: async (code: string) => {
      try {
        const result = await confirmationResult.confirm(code);
        clearPendingPhoneSignIn();
        return result;
      } catch (error) {
        throw mapConfirmError(error);
      }
    },
  };

  if (metadata) {
    stashPendingPhoneSignIn({
      confirmation,
      ...metadata,
      sentAt: Date.now(),
    });
  }

  return confirmation;
};
